import { LEVELS, PERKS } from '../data/classes.js';

/**
 * What the classes are worth, in the three shapes the three systems want.
 *
 * Three getters, and there will not be a fourth without a fourth class. This is
 * the seam the whole feature is built to have: `throwing.js`, `carry.js` and
 * `grading.js` each read one object of plain numbers and none of them has heard
 * of a class, a level, a minigame or a board. Take the classes out and those
 * three files keep working on the tier-zero row, which is the row they were
 * already hard-coding.
 *
 * Derived every read rather than cached. The tables are six entries long and
 * this is called perhaps twice a frame in the worst case; a cache here would
 * buy nothing and would have to be invalidated by a load, a reset and a pass,
 * which is three chances to be quietly wrong about how good the player is.
 */
export function createPerks({ classes }) {
  /**
   * A tier, clamped to the table.
   *
   * Every table is `LEVELS + 1` long and is indexed by levels passed, so a save
   * from a build with a sixth level, or a class that has been taken out of the
   * catalogue, lands on the last row rather than on `undefined`. An undefined
   * in here is a NaN in a velocity three files away, which is a bug that looks
   * like physics rather than like a save.
   */
  function tier(id) {
    return Math.max(0, Math.min(LEVELS, classes.passed(id)));
  }

  return {
    /**
     * The throw. Read by `sim/throwing.js` and by nothing else.
     *
     * `aim` is the half-angle of the cone a throw may be pulled into to find a
     * body, and at tier zero it is exactly zero: the throw goes where you were
     * looking, a near miss is a miss, and that is the game as it already was.
     * `range` scales the launch speed and leaves the loft alone, so a fire
     * extinguisher is still a lob.
     */
    get throwing() {
      const t = tier('trash-basketball');
      return { tier: t, aim: PERKS.throwing.aim[t], range: PERKS.throwing.range[t] };
    },

    /**
     * The hands. Read by `sim/carry.js` and by nothing else.
     *
     * `extra` is added to the cap on a stack and therefore cannot touch
     * anything the weight budget was already holding down. `grip` is the
     * fraction of a unit you keep when something walks into you; the hands
     * carry the remainder and shed a unit when it comes due, so it is a ratio
     * rather than a dice roll and a test can state exactly which bump spills.
     *
     * Your hands, and not a cart. A cart is built from the same module with the
     * same budget arithmetic and is deliberately handed no perk at all: a
     * trolley does not become steadier because you sat a class, and keeping the
     * perk off it is what makes "more in your arms" a sentence about arms.
     */
    get carry() {
      const t = tier('quick-refresh');
      return { tier: t, extra: PERKS.carry.extra[t], grip: PERKS.carry.grip[t] };
    },

    /**
     * The sheet. Read by `sim/grading.js` and by nothing else.
     *
     * One of the six marks, and the smallest of them. `forgiveness` scales the
     * sins the tidiness mark counts, so a room with nothing out of place scores
     * the same at every tier and the perk is only ever worth something to
     * somebody who left a mess.
     */
    get tidy() {
      const t = tier('sucky-sucky');
      return { tier: t, forgiveness: PERKS.tidy.forgiveness[t] };
    },

    /**
     * What a witness makes of a take. Read by `sim/perception.js` and by
     * nothing else.
     *
     * The one perk that is about being caught rather than about doing a job,
     * and it touches exactly one kind of claim: the player helping themselves.
     * A thrown bottle, a restricted room, a colleague's own pocketing are all
     * witnessed at full strength at every tier, because none of them is the
     * thing the class taught.
     */
    get hands() {
      const t = tier('quick-hands');
      return { tier: t, quiet: PERKS.hands.quiet[t] };
    },

    /** Everything at once, for the readouts and the headless checks. */
    get all() {
      return { throwing: this.throwing, carry: this.carry, tidy: this.tidy, hands: this.hands };
    },
  };
}

/**
 * The perks of a player who has never sat a class.
 *
 * Not a convenience: it is the row every system's old constant lived on, and
 * `bench.mjs` runs on it. Anything that wants to prove a perk changed something
 * measures against this.
 */
export const NO_PERKS = Object.freeze({
  throwing: Object.freeze({ tier: 0, aim: PERKS.throwing.aim[0], range: PERKS.throwing.range[0] }),
  carry: Object.freeze({ tier: 0, extra: PERKS.carry.extra[0], grip: PERKS.carry.grip[0] }),
  tidy: Object.freeze({ tier: 0, forgiveness: PERKS.tidy.forgiveness[0] }),
  hands: Object.freeze({ tier: 0, quiet: PERKS.hands.quiet[0] }),
});
